import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { checklistsApi, typesApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Search, 
  ClipboardList,
  Loader2,
  ChevronRight,
  Plus,
  FileText,
  Edit,
  Trash2,
  Copy,
  Download,
  AlertTriangle
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function Checklists() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [checklists, setChecklists] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, checklist: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [checklistsRes, typesRes] = await Promise.all([
        checklistsApi.getAll(),
        typesApi.getBuildingTypes()
      ]);
      setChecklists(checklistsRes.data);
      setBuildingTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.checklist) return;
    setDeleting(true);
    try {
      await checklistsApi.delete(deleteDialog.checklist.id);
      setChecklists(prev => prev.filter(c => c.id !== deleteDialog.checklist.id));
      setDeleteDialog({ open: false, checklist: null });
    } catch (error) {
      console.error('Error deleting checklist:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (checklist) => {
    try {
      const response = await checklistsApi.duplicate(checklist.id);
      setChecklists(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Error duplicating checklist:', error);
      alert('Fehler beim Duplizieren');
    }
  };

  const handleExport = async (checklist) => {
    try {
      const response = await checklistsApi.export(checklist.id);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${checklist.name.replace(/\s+/g, '_')}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting checklist:', error);
      alert('Fehler beim Exportieren');
    }
  };

  const filteredChecklists = checklists.filter(checklist => {
    const matchesSearch = 
      checklist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      checklist.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || checklist.building_type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  // Group by building type
  const groupedChecklists = filteredChecklists.reduce((acc, checklist) => {
    if (!acc[checklist.building_type]) {
      acc[checklist.building_type] = [];
    }
    acc[checklist.building_type].push(checklist);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500" data-testid="checklists-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Checklisten</h1>
            <p className="text-muted-foreground mt-1">Brandschutz-Prüfvorlagen nach Gebäudetyp</p>
          </div>
          {isAdmin && (
            <Button onClick={() => navigate('/admin/checklists/new')} data-testid="new-checklist-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neue Checkliste
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Checkliste suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
              data-testid="checklist-search-input"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-56 h-12" data-testid="type-filter">
              <SelectValue placeholder="Gebäudetyp filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Gebäudetypen</SelectItem>
              {buildingTypes.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Checklists */}
        {Object.keys(groupedChecklists).length > 0 ? (
          <Accordion type="multiple" className="space-y-4">
            {Object.entries(groupedChecklists).map(([buildingType, typeChecklists]) => (
              <AccordionItem 
                key={buildingType} 
                value={buildingType}
                className="border border-border/50 rounded-lg overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-accent/50">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="h-5 w-5 text-primary" />
                    <span className="font-heading font-semibold text-lg">{buildingType}</span>
                    <Badge variant="secondary" className="ml-2">
                      {typeChecklists.length} Vorlage(n)
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <div className="space-y-3 pt-2">
                    {typeChecklists.map((checklist) => (
                      <Card 
                        key={checklist.id} 
                        className="border-border/50 card-hover"
                        data-testid={`checklist-${checklist.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{checklist.name}</h4>
                                {checklist.is_default && (
                                  <Badge variant="outline" className="text-xs">Standard</Badge>
                                )}
                              </div>
                              {checklist.description && (
                                <p className="text-sm text-muted-foreground mb-2">
                                  {checklist.description}
                                </p>
                              )}
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {checklist.items?.length || 0} Prüfpunkte
                                </span>
                                <span>•</span>
                                <span>Erstellt: {formatDate(checklist.created_at)}</span>
                              </div>
                            </div>
                            
                            {/* Admin Actions */}
                            {isAdmin && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Bearbeiten"
                                  onClick={() => navigate(`/admin/checklists/${checklist.id}`)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Duplizieren"
                                  onClick={() => handleDuplicate(checklist)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Exportieren"
                                  onClick={() => handleExport(checklist)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Löschen"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setDeleteDialog({ open: true, checklist })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {/* Preview of items */}
                          {checklist.items?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <p className="text-xs text-muted-foreground mb-2">Kategorien:</p>
                              <div className="flex flex-wrap gap-1">
                                {[...new Set(checklist.items.map(i => i.category))].slice(0, 5).map((category) => (
                                  <Badge key={category} variant="secondary" className="text-xs">
                                    {category}
                                  </Badge>
                                ))}
                                {[...new Set(checklist.items.map(i => i.category))].length > 5 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{[...new Set(checklist.items.map(i => i.category))].length - 5}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <Card className="border-border/50">
            <CardContent className="py-16">
              <div className="text-center">
                <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-muted-foreground">
                  {searchTerm || typeFilter !== 'all' 
                    ? 'Keine Checklisten gefunden' 
                    : 'Noch keine Checklisten vorhanden'}
                </p>
                {isAdmin && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate('/admin/checklists/new')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Erste Checkliste erstellen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, checklist: deleteDialog.checklist })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Checkliste löschen?
            </DialogTitle>
            <DialogDescription>
              Möchten Sie die Checkliste "{deleteDialog.checklist?.name}" wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, checklist: null })}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
